import React, { useEffect, useState, useRef, useCallback } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { allAPiServicesCall } from "../../services/apiServices";
import { getAuthConfig, getFormDataConfig } from "../../services/apiUtils";
import { toast } from "react-toastify";
import JoditEditor from 'jodit-react';
import { extractObjectId } from "../../utils/utils";

function EditNewsModal({ show, handleClose, newsDetails, handleFormSubmit }) {
  const [updatedData, setUpdatedData] = useState({
    newsId: extractObjectId(newsDetails._id),
    title: newsDetails.title,
    author: newsDetails.author,
    description: newsDetails.description,
    publishDate: newsDetails.publishDate,
    coverImg: newsDetails.coverImg,
    coverImgCreds: newsDetails.coverImgCreds,
    readTime: newsDetails.readTime || "",
    instagram: newsDetails.instagram || "",
    authorPic: newsDetails.authorPic || "",
    authorDesignation: newsDetails.authorDesignation || "",
    priority: newsDetails.priority,
    primaryTag: newsDetails.primaryTag || "Music News",
  });

  const isGovernedArticle = Boolean(
    updatedData.workflowStatus || updatedData.evidenceRunId,
  );

  useEffect(() => {
    if (newsDetails) {
      setUpdatedData({ ...newsDetails });
      setSelectedGenres(
        newsDetails.genres?.length
          ? newsDetails.genres
          : newsDetails.genre
            ? [newsDetails.genre]
            : [],
      );
      setTaggedArtists(newsDetails.taggedArtists || []);
      // Set tags - they are stored as names (strings)
      setSelectedTags(newsDetails.tags || []);
      // Reset search state when switching news
      setArtistSearch('');
      setArtistResults([]);
      setShowArtistDropdown(false);
    }
  }, [newsDetails]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const [newImageFile, setNewImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [taggedArtists, setTaggedArtists] = useState([]);
  const [artistSearch, setArtistSearch] = useState('');
  const [artistResults, setArtistResults] = useState([]);
  const [showArtistDropdown, setShowArtistDropdown] = useState(false);
  const [genres, setGenres] = useState([]);

  // Tags state
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [newTagInput, setNewTagInput] = useState('');
  const [newTagType, setNewTagType] = useState('topic');
  const [addingTag, setAddingTag] = useState(false);
  const [tagError, setTagError] = useState(null);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [showTagList, setShowTagList] = useState(false);

  // Genre state for search and add
  const [newGenreInput, setNewGenreInput] = useState('');
  const [addingGenre, setAddingGenre] = useState(false);
  const [genreSearchQuery, setGenreSearchQuery] = useState('');
  const [showGenreList, setShowGenreList] = useState(false);

  const TAG_TYPES = [
    { value: 'genre', label: 'Genre' },
    { value: 'mood', label: 'Mood' },
    { value: 'topic', label: 'Topic' },
    { value: 'era', label: 'Era' },
    { value: 'custom', label: 'Custom' },
  ];

  const editor = useRef(null);
  const searchTimeoutRef = useRef(null);

  const fetchTags = async () => {
    try {
      const response = await allAPiServicesCall.getTags({}, getAuthConfig());
      if (response?.data?.tags) {
        setTags(response.data.tags);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  useEffect(() => {
    const fetchGenres = async () => {
      try {
        const response = await allAPiServicesCall.getGenres({}, getAuthConfig());
        // Sort genres alphabetically by genreName
        const sortedGenres = (response.data || []).sort((a, b) =>
          a.genreName.toLowerCase().localeCompare(b.genreName.toLowerCase())
        );
        setGenres(sortedGenres);
      } catch (error) {
        console.error('Error fetching genres:', error);
      }
    };

    fetchGenres();
    fetchTags();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdatedData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setNewImageFile(file);
    const objectUrl = URL.createObjectURL(file);
    setImagePreview(objectUrl);
  };

  const handleMultipleGenreChange = (genreName) => {
    setSelectedGenres(prev => {
      if (prev.includes(genreName)) {
        return prev.filter(g => g !== genreName);
      } else {
        return [...prev, genreName];
      }
    });
  };

  const handleTagChange = (tagName) => {
    setSelectedTags(prev => {
      if (prev.includes(tagName)) {
        return prev.filter(name => name !== tagName);
      } else {
        return [...prev, tagName];
      }
    });
  };

  const handleAddNewTag = async () => {
    const trimmedName = newTagInput.trim();
    if (!trimmedName) return;

    // Check if tag already exists
    const existingTag = tags.find(t => t.name.toLowerCase() === trimmedName.toLowerCase());
    if (existingTag) {
      // Auto-select the existing tag instead of showing error
      if (!selectedTags.includes(existingTag.name)) {
        setSelectedTags(prev => [...prev, existingTag.name]);
        toast.success(`"${existingTag.name}" selected`);
      } else {
        toast.info(`"${existingTag.name}" is already selected`);
      }
      setNewTagInput('');
      setTagSearchQuery('');
      setTagError(null);
      return;
    }

    setAddingTag(true);
    try {
      const response = await allAPiServicesCall.createTag({
        name: trimmedName,
        type: newTagType,
        isActive: true
      }, getAuthConfig());
      if (response?.success) {
        // Refresh tags list
        await fetchTags();
        // Auto-select the new tag by name
        if (response.data?.name) {
          setSelectedTags(prev => [...prev, response.data.name]);
          toast.success(`"${response.data.name}" created and selected`);
        }
        setNewTagInput('');
        setTagSearchQuery('');
        setTagError(null);
      }
    } catch (error) {
      console.error('Error adding tag:', error);
      setTagError('Failed to add tag');
    } finally {
      setAddingTag(false);
    }
  };

  const handleAddNewGenre = async () => {
    const trimmedGenre = newGenreInput.trim();
    if (!trimmedGenre) return;

    // Check if genre already exists
    const existingGenre = genres.find(g => g.genreName.toLowerCase() === trimmedGenre.toLowerCase());
    if (existingGenre) {
      // Auto-select the existing genre instead of showing error
      if (!selectedGenres.includes(existingGenre.genreName)) {
        setSelectedGenres(prev => [...prev, existingGenre.genreName]);
        toast.success(`"${existingGenre.genreName}" selected`);
      } else {
        toast.info(`"${existingGenre.genreName}" is already selected`);
      }
      setNewGenreInput('');
      setGenreSearchQuery('');
      return;
    }

    setAddingGenre(true);
    try {
      const response = await allAPiServicesCall.addGenres([trimmedGenre], getAuthConfig());
      if (response?.success) {
        // Refresh genres list
        const genreResponse = await allAPiServicesCall.getGenres({}, getAuthConfig());
        const sortedGenres = (genreResponse.data || []).sort((a, b) =>
          a.genreName.toLowerCase().localeCompare(b.genreName.toLowerCase())
        );
        setGenres(sortedGenres);
        // Auto-select the new genre
        setSelectedGenres(prev => [...prev, trimmedGenre]);
        setNewGenreInput('');
        setGenreSearchQuery('');
        toast.success(`"${trimmedGenre}" created and selected`);
      }
    } catch (error) {
      console.error('Error adding genre:', error);
    } finally {
      setAddingGenre(false);
    }
  };

  const handleArtistSearch = useCallback((searchTerm) => {
    setArtistSearch(searchTerm);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchTerm.length < 2) {
      setArtistResults([]);
      setShowArtistDropdown(false);
      return;
    }

    // Debounce: wait 300ms before searching
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await allAPiServicesCall.getSearchArtist(
          { query: searchTerm },
          getAuthConfig()
        );
        if (response?.data) {
          setArtistResults(response.data);
          setShowArtistDropdown(true);
        }
      } catch (error) {
        console.error('Error searching artists:', error);
      }
    }, 300);
  }, []);

  const handleAddArtist = (artist) => {
    if (!taggedArtists.find(a => extractObjectId(a._id) === extractObjectId(artist._id))) {
      setTaggedArtists([...taggedArtists, artist]);
    }
    setArtistSearch('');
    setArtistResults([]);
    setShowArtistDropdown(false);
  };

  const handleRemoveArtist = (artistId) => {
    setTaggedArtists(taggedArtists.filter(a => extractObjectId(a._id) !== extractObjectId(artistId)));
  };

  const uploadImageAndGetUrl = async () => {
    if (!newImageFile) return null;
    try {
      const formData = new FormData();
      formData.append("image", newImageFile);

      const response = await allAPiServicesCall.uploadNewsImage(
        formData,
        getFormDataConfig()
      );
      return response.data.path;
    } catch (error) {
      console.error("Image upload failed:", error.response?.data || error);
      return null;
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    let imageUrl = updatedData.coverImg;

    if (newImageFile) {
      const uploadedImageUrl = await uploadImageAndGetUrl();
      if (uploadedImageUrl) {
        imageUrl = uploadedImageUrl;
      }
    }

    const finalUpdatedData = {
      ...updatedData,
      coverImg: imageUrl,
      genre: selectedGenres[0] || updatedData.genre || undefined,
      genres: selectedGenres,
      tags: selectedTags,
      taggedArtists: taggedArtists.map(artist => extractObjectId(artist._id))
    };
    const updatedataNeswData = {
        ...finalUpdatedData,
        newsId: extractObjectId(newsDetails._id)
      }

    try {
      const updateResponse = await allAPiServicesCall.putUpdateNews(
        updatedataNeswData,
        getAuthConfig()
      );
      console.log(updateResponse);
      if (updateResponse && updateResponse.status === 200) {
        toast.success("News updated successfully");
        handleClose();
      } else {
        toast.error("Failed to update news");
      }
    } catch (error) {
      console.error("Failed to update news:", error.response?.data || error);
      toast.error("An error occurred while updating the news");
    }
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Edit News</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={onSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Genres:</Form.Label>

            {/* Search/Add Genre Input */}
            <div className="input-group mb-2">
              <Form.Control
                type="text"
                placeholder="Search or add genre..."
                value={genreSearchQuery || newGenreInput}
                onChange={(e) => {
                  setGenreSearchQuery(e.target.value);
                  setNewGenreInput(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddNewGenre();
                  }
                }}
                disabled={addingGenre}
              />
              <Button
                variant="outline-primary"
                onClick={handleAddNewGenre}
                disabled={addingGenre || !newGenreInput.trim()}
              >
                {addingGenre ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                ) : (
                  'Add'
                )}
              </Button>
            </div>
            <Form.Text className="text-muted d-block mb-2">Type to search or add a new genre.</Form.Text>

            {/* Selected Genres Display */}
            {selectedGenres.length > 0 && (
              <div className="mb-2">
                <div className="d-flex flex-wrap gap-1">
                  {selectedGenres.map((genre) => (
                    <span key={genre} className="badge bg-primary d-flex align-items-center gap-1">
                      {genre}
                      <button
                        type="button"
                        className="btn-close btn-close-white"
                        style={{ fontSize: '0.5rem' }}
                        onClick={() => handleMultipleGenreChange(genre)}
                        aria-label="Remove"
                      ></button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Toggle to show/hide genre list */}
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-decoration-none"
              onClick={() => setShowGenreList(!showGenreList)}
            >
              {showGenreList ? '▼ Hide genre list' : '▶ Browse all genres'}
            </button>

            {/* Collapsible Genre List */}
            {showGenreList && (
              <div className="border rounded p-3 mt-2" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                {genres
                  .filter(genre =>
                    !genreSearchQuery ||
                    genre.genreName.toLowerCase().includes(genreSearchQuery.toLowerCase())
                  )
                  .map((genre) => (
                    <div key={extractObjectId(genre._id)} className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        value={genre.genreName}
                        id={`modal-genre-${extractObjectId(genre._id)}`}
                        checked={selectedGenres.includes(genre.genreName)}
                        onChange={() => handleMultipleGenreChange(genre.genreName)}
                      />
                      <label className="form-check-label" htmlFor={`modal-genre-${extractObjectId(genre._id)}`}>
                        {genre.genreName}
                      </label>
                    </div>
                  ))}
                {genres.filter(genre =>
                  !genreSearchQuery ||
                  genre.genreName.toLowerCase().includes(genreSearchQuery.toLowerCase())
                ).length === 0 && (
                  <p className="text-muted mb-0">No genres found. Click "Add" to create "{genreSearchQuery}"</p>
                )}
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Tag Artists:</Form.Label>
            <div className="position-relative">
              <Form.Control
                type="text"
                value={artistSearch}
                onChange={(e) => handleArtistSearch(e.target.value)}
                placeholder="Search for artists to tag..."
              />
              {showArtistDropdown && artistResults.length > 0 && (
                <div className="position-absolute w-100 bg-white border rounded shadow-sm mt-1" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                  {artistResults.map((artist) => (
                    <div
                      key={extractObjectId(artist._id)}
                      className="p-2"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleAddArtist(artist)}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      {artist.artist_name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {taggedArtists.length > 0 && (
              <div className="mt-2">
                <label className="form-label">Tagged Artists:</label>
                <div className="d-flex flex-wrap gap-2">
                  {taggedArtists.map((artist) => (
                    <span key={extractObjectId(artist._id) || artist.artist_id} className="badge bg-primary d-flex align-items-center gap-1">
                      {artist.artist_name}
                      <button
                        type="button"
                        className="btn-close btn-close-white"
                        style={{ fontSize: '0.6rem' }}
                        onClick={() => handleRemoveArtist(extractObjectId(artist._id) || artist.artist_id)}
                        aria-label="Remove"
                      ></button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Form.Group>

          {/* Tags Section */}
          <Form.Group className="mb-3">
            <Form.Label>Tags:</Form.Label>

            {/* Search/Add Tag Input */}
            <div className="row g-2 mb-2">
              <div className="col-6">
                <Form.Control
                  type="text"
                  placeholder="Search or add tag..."
                  value={tagSearchQuery || newTagInput}
                  onChange={(e) => {
                    setTagSearchQuery(e.target.value);
                    setNewTagInput(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddNewTag();
                    }
                  }}
                  disabled={addingTag}
                />
              </div>
              <div className="col-3">
                <Form.Select
                  value={newTagType}
                  onChange={(e) => setNewTagType(e.target.value)}
                  disabled={addingTag}
                  size="sm"
                >
                  {TAG_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-3">
                <Button
                  variant="outline-primary"
                  className="w-100"
                  onClick={handleAddNewTag}
                  disabled={addingTag || !newTagInput.trim()}
                >
                  {addingTag ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  ) : (
                    'Add'
                  )}
                </Button>
              </div>
            </div>
            <Form.Text className="text-muted d-block mb-2">Type to search or add a new tag.</Form.Text>
            {tagError && <small className="text-danger d-block mb-2">{tagError}</small>}

            {/* Selected Tags Display */}
            {selectedTags.length > 0 && (
              <div className="mb-2">
                <div className="d-flex flex-wrap gap-1">
                  {selectedTags.map((tagName) => {
                    const tag = tags.find(t => t.name === tagName);
                    return (
                      <span
                        key={tagName}
                        className="badge d-flex align-items-center gap-1"
                        style={{ backgroundColor: tag?.color || '#6366f1' }}
                      >
                        {tagName}
                        <button
                          type="button"
                          className="btn-close btn-close-white"
                          style={{ fontSize: '0.5rem' }}
                          onClick={() => handleTagChange(tagName)}
                          aria-label="Remove"
                        ></button>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Toggle to show/hide tag list */}
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-decoration-none"
              onClick={() => setShowTagList(!showTagList)}
            >
              {showTagList ? '▼ Hide tag list' : '▶ Browse all tags'}
            </button>

            {/* Collapsible Tag List */}
            {showTagList && (
              <div className="border rounded p-3 mt-2" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                {tags.length === 0 ? (
                  <p className="text-muted mb-0">No tags available. Use the input above to add one.</p>
                ) : (
                  tags
                    .filter(tag => tag.isActive)
                    .filter(tag =>
                      !tagSearchQuery ||
                      tag.name.toLowerCase().includes(tagSearchQuery.toLowerCase())
                    )
                    .map((tag) => (
                      <div key={extractObjectId(tag._id)} className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          value={tag.name}
                          id={`modal-tag-${extractObjectId(tag._id)}`}
                          checked={selectedTags.includes(tag.name)}
                          onChange={() => handleTagChange(tag.name)}
                        />
                        <label className="form-check-label" htmlFor={`modal-tag-${extractObjectId(tag._id)}`}>
                          <span className="badge me-1" style={{ backgroundColor: tag.color || '#6366f1' }}>
                            {tag.type}
                          </span>
                          {tag.name}
                        </label>
                      </div>
                    ))
                )}
                {tags.filter(tag => tag.isActive).filter(tag =>
                  !tagSearchQuery ||
                  tag.name.toLowerCase().includes(tagSearchQuery.toLowerCase())
                ).length === 0 && tags.length > 0 && (
                  <p className="text-muted mb-0">No tags found. Click "Add" to create "{tagSearchQuery}"</p>
                )}
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              name="title"
              value={updatedData.title}
              onChange={handleInputChange}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Author</Form.Label>
            <Form.Control
              type="text"
              name="author"
              value={updatedData.author}
              onChange={handleInputChange}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <JoditEditor
              ref={editor}
              name="description"
              value={updatedData.description}
              onChange={(newContent) =>
                setUpdatedData((prevData) => ({
                  ...prevData,
                  description: newContent,
                }))
              }
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Read Time</Form.Label>
            <Form.Control
              type="text"
              name="readTime"
              value={updatedData.readTime}
              onChange={handleInputChange}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Instagram</Form.Label>
            <Form.Control
              type="text"
              name="instagram"
              value={updatedData.instagram}
              onChange={handleInputChange}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Photo Credits</Form.Label>
            <Form.Control
              type="text"
              name="coverImgCreds"
              value={updatedData.coverImgCreds}
              onChange={handleInputChange}
            />
          </Form.Group>

          {!isGovernedArticle && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>
                  Image Rights Status{" "}
                  <small className="text-muted">(optional for manual publishing)</small>
                </Form.Label>
                <Form.Select
                  name="coverImgRightsStatus"
                  value={updatedData.coverImgRightsStatus || ""}
                  onChange={handleInputChange}
                >
                  <option value="">-- Select rights status --</option>
                  <option value="verified">Verified</option>
                  <option value="pending">Pending</option>
                  <option value="rejected">Rejected</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Image Rights Holder{" "}
                  <small className="text-muted">(optional for manual publishing)</small>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="coverImgRightsHolder"
                  value={updatedData.coverImgRightsHolder || ""}
                  onChange={handleInputChange}
                  maxLength={200}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Rights Evidence URL{" "}
                  <small className="text-muted">(optional for manual publishing)</small>
                </Form.Label>
                <Form.Control
                  type="url"
                  name="coverImgRightsEvidenceRef"
                  value={updatedData.coverImgRightsEvidenceRef || ""}
                  onChange={handleInputChange}
                  maxLength={500}
                />
              </Form.Group>
            </>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Author Picture URL</Form.Label>
            {updatedData.authorPic && (
              <div className="mb-3">
                <img
                  src={imagePreview || updatedData.authorPic}
                  alt="News"
                  style={{ width: "100%", height: "auto", marginBottom: "10px",objectFit:"cover" }}
                />
              </div>
            )}
            <Form.Control
              type="text"
              name="authorPic"
              value={updatedData.authorPic}
              onChange={handleInputChange}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Author Designation</Form.Label>
            <Form.Control
              type="text"
              name="authorDesignation"
              value={updatedData.authorDesignation}
              onChange={handleInputChange}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>News Image</Form.Label>
            {updatedData.coverImg && (
              <div className="mb-3">
                <img
                  src={imagePreview || updatedData.coverImg}
                  alt="News"
                  style={{ width: "100%", height: "auto", marginBottom: "10px",objectFit:"cover" }}
                />
              </div>
            )}
            <Form.Control
              type="file"
              name="coverImg"
              onChange={handleFileChange}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Publish Date</Form.Label>
            <Form.Control
              type="date"
              name="publishDate"
              value={updatedData.publishDate}
              onChange={handleInputChange}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Priority</Form.Label>
            <Form.Control
              type="number"
              name="priority"
              value={updatedData.priority}
              onChange={handleInputChange}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Primary Tag</Form.Label>
            <Form.Control
              type="text"
              name="primaryTag"
              value={updatedData.primaryTag}
              onChange={handleInputChange}
              placeholder="e.g., Music News, LyricsWeb News"
            />
            <Form.Text className="text-muted">
              Tag displayed on the news card (default: Music News)
            </Form.Text>
          </Form.Group>

          <Button variant="primary" type="submit" className="float-end">
            Save Changes
          </Button>
          <Button variant="danger" onClick={handleClose}>
            Cancel
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default EditNewsModal;
